import { html, raw } from '../lib/html.js';
import { onlyDigits } from '../lib/masks.js';
import { qrSvg } from '../lib/qr.js';
import { busy, confirm, toast, toastError, toastSuccess } from '../lib/ui.js';
import { fmtKm, meterShort, meterUnit, setMeterUnit } from '../services/maintenances.js';

const hours = () => meterUnit() === 'HOURS';
import { vehicles } from '../services/vehicles.js';
import { Page, go } from './base.js';

const fmtDay = (v) => new Date(v).toLocaleDateString('pt-BR');

/**
 * /vehicles/:vehicleId/vender — "Vendi este veículo" (só o dono comprovado).
 *   1. informa a km de hoje e gera o link/QR de transferência (vale 14 dias)
 *   2. mostra o QR ao comprador ou manda o link (WhatsApp / copiar)
 *   3. o veículo continua com o vendedor até o comprador aceitar
 * O link só aparece na hora em que é gerado (o servidor guarda só o hash): para mostrar de novo, gera outro.
 * "Vendi, o comprador não usa o app": o vendedor sai e o veículo fica sem dono (histórico guardado).
 * Enquanto o link estiver na tela, consulta a venda a cada 3 s (como a autorização de empresa): mostra quando o
 * comprador abriu o link e troca a tela sozinha quando ele aceita (o veículo sai da garagem) ou recusa.
 */
const POLL_MS = 3000;
class VehicleSellPage extends Page {
  template() {
    return html`
      <ion-header class="ion-no-border">
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Vender o veículo</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <div class="narrow body wiz"></div>
      </ion-content>`;
  }

  get vid() {
    if (this.vehicleId) return String(this.vehicleId);
    return decodeURIComponent(location.pathname.match(/\/vehicles\/([^/?#]+)/)?.[1] ?? '');
  }

  setup() {
    this.addEventListener('ionViewWillLeave', () => this.stopWatch());
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el) this.action(el.dataset.action, el);
    });
  }

  async onEnter() {
    this.$('ion-back-button').defaultHref = `/vehicles/${this.vid}`;
    this.stopWatch();
    this.link = null;
    const body = this.$('.body');
    body.innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      const [v, t] = await Promise.all([vehicles.get(this.vid), vehicles.transfer(this.vid)]);
      setMeterUnit(v.vehicle.meterUnit);
      this.label = v.access?.nickname || [v.vehicle.brand, v.vehicle.model].filter(Boolean).join(' ') || v.vehicle.plateFormatted || `Série ${v.vehicle.serialNumber ?? ''}`;
      this.state = t;
      this.render();
    } catch (err) {
      body.innerHTML = html`<div class="center"><ion-icon name="alert-circle-outline" class="empty-icon"></ion-icon><p>${err.message}</p></div>`.value;
    }
  }

  render() {
    const t = this.state;
    const body = this.$('.body');
    if (!t.canSell) {
      body.innerHTML = html`
        <div class="wiz-icon"><ion-icon name="lock-closed-outline"></ion-icon></div>
        <h1 class="wiz-title">Ainda não dá para vender pelo app</h1>
        <p class="wiz-lead">${t.blocked === 'OWNER_NOT_VERIFIED'
          ? 'Envie o CRLV-e no seu nome primeiro. Depois que a análise aprovar, você pode passar o veículo adiante por aqui.'
          : 'Só o dono do veículo pode vender pelo app.'}</p>`.value;
      return;
    }
    if (this.link) return this.renderLink();
    if (t.transfer?.status === 'PENDING') return this.renderPending();
    this.renderForm();
  }

  renderForm() {
    const known = this.state.knownKm;
    this.$('.body').innerHTML = html`
      <div class="wiz-icon"><ion-icon name="swap-horizontal-outline"></ion-icon></div>
      <h1 class="wiz-title">Vendeu o ${this.label}?</h1>
      <p class="wiz-lead">Passe o veículo para o comprador pelo app. O histórico de manutenções vai junto com ele.</p>
      <ol class="steps-list">
        <li><strong>${hours() ? 'Informe o horímetro de hoje.' : 'Informe a km de hoje.'}</strong> ${hours() ? 'Ele vira' : 'Ela vira'} o ponto de partida dos lembretes do novo dono.</li>
        <li><strong>Mostre o QR ou mande o link.</strong> O comprador abre no celular dele e aceita.</li>
        <li><strong>Pronto.</strong> O veículo sai da sua garagem e entra na dele. Até lá, continua com você.</li>
      </ol>
      <ion-input class="km-input" aria-label="${hours() ? 'Horímetro na venda' : 'Km na venda'}" fill="outline" inputmode="numeric" enterkeyhint="done" placeholder="${known === null ? '0' : Number(known).toLocaleString('pt-BR')}" maxlength="9">
        <span slot="end" class="km-unit">${meterShort()}</span>
      </ion-input>
      <p class="field-error km-error" role="alert"></p>
      ${known !== null ? html`<p class="wiz-hint left">${hours() ? 'Último horímetro registrado' : 'Última km registrada'}: ${fmtKm(known)}</p>` : ''}
      <div class="wiz-foot">
        <ion-button expand="block" data-action="generate"><ion-icon slot="start" name="qr-code-outline"></ion-icon>Gerar link de transferência</ion-button>
        <ion-button expand="block" fill="clear" color="medium" data-action="offline">Vendi, mas o comprador não usa o app</ion-button>
      </div>`.value;
    const input = this.$('.km-input');
    input.addEventListener('ionInput', (e) => {
      const d = onlyDigits(e.target.value).slice(0, 7);
      e.target.value = d ? Number(d).toLocaleString('pt-BR') : '';
      this.$('.km-error').textContent = '';
    });
  }

  renderLink() {
    const { url, transfer } = this.link;
    this.$('.body').innerHTML = html`
      <h1 class="wiz-title">Mostre para o comprador</h1>
      <p class="wiz-lead">Ele lê o QR com a câmera do celular, entra no TicketCar e aceita. Se não estiver junto, mande o link.</p>
      <div class="qr sale-qr">${raw(qrSvg(url))}</div>
      <div class="sale-actions">
        <ion-button expand="block" data-action="whatsapp"><ion-icon slot="start" name="logo-whatsapp"></ion-icon>Enviar pelo WhatsApp</ion-button>
        <ion-button expand="block" fill="outline" data-action="copy"><ion-icon slot="start" name="copy-outline"></ion-icon>Copiar link</ion-button>
      </div>
      <div class="panel">
        <div class="kv"><span>${hours() ? 'Horímetro na venda' : 'Km na venda'}</span><strong>${fmtKm(transfer.km)}</strong></div>
        <div class="kv"><span>Link vale até</span><strong>${fmtDay(transfer.expiresAt)}</strong></div>
      </div>
      ${waitPanel(transfer)}
      <p class="wiz-hint left">O veículo continua na sua garagem até o comprador aceitar. Esta tela atualiza sozinha.</p>
      <ion-button expand="block" fill="clear" color="danger" data-action="cancel">Cancelar venda</ion-button>`.value;
    this.watch(transfer.id);
  }

  renderPending() {
    const t = this.state.transfer;
    this.$('.body').innerHTML = html`
      <div class="wiz-icon"><ion-icon name="hourglass-outline"></ion-icon></div>
      <h1 class="wiz-title">Venda em andamento</h1>
      <p class="wiz-lead">O link de transferência vale até ${fmtDay(t.expiresAt)} (${hours() ? 'horímetro' : 'km'} ${fmtKm(t.km)}). O veículo continua com você até o comprador aceitar.</p>
      ${waitPanel(t)}
      <p class="wiz-hint left">Por segurança, o link só aparece na hora em que é gerado. Para mostrar o QR de novo, gere outro: o anterior deixa de valer.</p>
      <div class="wiz-foot">
        <ion-button expand="block" data-action="regenerate"><ion-icon slot="start" name="qr-code-outline"></ion-icon>Mostrar QR (gera outro link)</ion-button>
        <ion-button expand="block" fill="clear" color="danger" data-action="cancel">Cancelar venda</ion-button>
      </div>`.value;
    this.watch(t.id);
  }

  /** Consulta a venda a cada POLL_MS enquanto a tela estiver aberta. */
  watch(transferId) {
    this.stopWatch();
    const vid = this.vid;
    this.poll = setInterval(async () => {
      try {
        const r = await vehicles.transferStatus(vid, transferId);
        if (this.vid !== vid) return;
        this.onStatus(r);
      } catch {
        /* sem rede: tenta de novo no próximo ciclo */
      }
    }, POLL_MS);
  }

  stopWatch() {
    clearInterval(this.poll);
    this.poll = null;
  }

  onStatus({ transfer: t, buyer }) {
    if (t.status === 'PENDING') {
      const text = this.$('.sale-wait-text');
      if (text && t.openedAt) text.textContent = 'O comprador abriu o link. Aguardando ele aceitar…';
      return;
    }
    this.stopWatch();
    this.link = null;
    if (t.status === 'ACCEPTED') return this.renderSold(buyer);
    if (t.status === 'DECLINED') return this.renderEnded({ icon: 'alert-circle-outline', title: 'O comprador recusou', lead: `${buyer?.firstName ?? 'O comprador'} recusou a transferência. O ${this.label} continua com você.`, again: true });
    if (t.status === 'EXPIRED') return this.renderEnded({ icon: 'time-outline', title: 'O link venceu', lead: 'Ninguém aceitou a transferência no prazo. Gere outro link quando estiver com o comprador.', again: true });
    // CANCELED: cancelado em outro aparelho, substituído por outro link ou o veículo mudou de dono por outro caminho
    this.renderEnded({ icon: 'alert-circle-outline', title: 'Este link não vale mais', lead: t.canceledReason === 'REPLACED' ? 'Um link novo foi gerado em outro aparelho.' : 'A venda foi cancelada.', again: false });
  }

  renderSold(buyer) {
    toastSuccess('Venda concluída!');
    this.$('.body').innerHTML = html`
      <div class="wiz-done"><ion-icon name="checkmark-circle-outline"></ion-icon></div>
      <h1 class="wiz-title">Venda concluída</h1>
      <p class="wiz-lead">${buyer?.firstName ?? 'O comprador'} aceitou a transferência do ${this.label}. O veículo saiu da sua garagem e o histórico de manutenções foi junto com ele.</p>
      <div class="wiz-foot"><ion-button expand="block" data-action="home">Ir para o início</ion-button></div>`.value;
  }

  renderEnded({ icon, title, lead, again }) {
    this.state = { ...this.state, transfer: null };
    this.$('.body').innerHTML = html`
      <div class="wiz-icon"><ion-icon name="${icon}"></ion-icon></div>
      <h1 class="wiz-title">${title}</h1>
      <p class="wiz-lead">${lead}</p>
      <div class="wiz-foot">
        ${again ? html`<ion-button expand="block" data-action="new"><ion-icon slot="start" name="qr-code-outline"></ion-icon>Gerar outro link</ion-button>` : ''}
        <ion-button expand="block" fill="${again ? 'clear' : 'outline'}" data-action="vehicle">Voltar ao veículo</ion-button>
      </div>`.value;
  }

  kmTyped() {
    const raw = onlyDigits(this.$('.km-input')?.value ?? '');
    return raw ? Number(raw) : null;
  }

  async generate(button, km) {
    await busy(button, async () => {
      try {
        const r = await vehicles.sell(this.vid, km);
        this.link = { url: r.url ?? `${location.origin}/transferir/${r.token}`, transfer: r.transfer };
        this.state = { ...this.state, transfer: r.transfer };
        this.render();
      } catch (e) {
        const err = this.$('.km-error');
        if (err && e.code === 'KM_LOWER') err.textContent = e.message;
        else toastError(e.message);
      }
    });
  }

  async action(name, el) {
    if (name === 'home') return go('/home', 'root');
    if (name === 'vehicle') return go(`/vehicles/${this.vid}`, 'back');
    if (name === 'new') return this.renderForm();
    if (name === 'generate') {
      const km = this.kmTyped();
      if (km === null) return (this.$('.km-error').textContent = hours() ? 'Informe o horímetro no dia da venda.' : 'Informe a km do painel no dia da venda.');
      return this.generate(el, km);
    }
    if (name === 'regenerate') return this.generate(el, this.state.transfer.km);
    if (name === 'copy') {
      try {
        await navigator.clipboard.writeText(this.link.url);
        toastSuccess('Link copiado.');
      } catch {
        toast(this.link.url, 'dark', 8000);
      }
      return;
    }
    if (name === 'whatsapp') {
      const text = `Transferência do ${this.label} no TicketCar: abra o link para receber o veículo com o histórico de manutenções. ${this.link.url}`;
      return window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
    if (name === 'cancel') {
      if (!(await confirm({ header: 'Cancelar venda?', message: 'O link deixa de funcionar e o veículo continua com você.', confirmText: 'Cancelar venda', cancelText: 'Voltar', destructive: true }))) return;
      this.stopWatch();
      try {
        await vehicles.cancelSale(this.vid);
        toast('Venda cancelada.');
        return go(`/vehicles/${this.vid}`, 'back');
      } catch (e) {
        return toastError(e.message);
      }
    }
    if (name === 'offline') {
      const km = this.kmTyped();
      const sure = await confirm({
        header: 'O veículo sai da sua garagem',
        message: 'Você, a pessoa autorizada e as empresas perdem o acesso agora. O histórico fica guardado no veículo: quando o comprador entrar no TicketCar e enviar o CRLV-e dele, recebe tudo.',
        confirmText: 'Vendi, tirar da garagem',
        cancelText: 'Voltar',
        destructive: true,
      });
      if (!sure) return;
      try {
        await vehicles.soldOffline(this.vid, km ?? undefined);
        toastSuccess('Pronto. O veículo saiu da sua garagem.');
        return go('/home', 'root');
      } catch (e) {
        const err = this.$('.km-error');
        if (err && e.code === 'KM_LOWER') err.textContent = e.message;
        else toastError(e.message);
      }
    }
  }
}

/** "Aguardando o comprador": muda para "abriu o link" quando ele lê o QR. */
function waitPanel(t) {
  return html`
    <div class="panel sale-wait" role="status" aria-live="polite">
      <ion-spinner name="dots"></ion-spinner>
      <span class="sale-wait-text">${t?.openedAt ? 'O comprador abriu o link. Aguardando ele aceitar…' : 'Aguardando o comprador ler o QR ou abrir o link…'}</span>
    </div>`;
}

customElements.define('page-vehicle-sell', VehicleSellPage);
